import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { adsApi } from '@/api/client';
import { formatCurrencyValue, formatPercentValue } from '@/api/keywordMetrics';
import { statusLabel, type DailySpendStatus } from '@/utils/spendStatus';
import type { DailySpendPayload } from '@/types/dailySpend';

function statusBadgeVariant(status: DailySpendStatus): BadgeVariant {
  if (status === 'green') return 'success';
  if (status === 'yellow') return 'warning';
  if (status === 'red') return 'danger';
  return 'neutral';
}

export function DailySpendPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('profiles.write');
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [budgetInput, setBudgetInput] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['daily-spend', profileId],
    queryFn: async () => {
      const res = await adsApi.getDailySpend(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load daily spend');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  useEffect(() => {
    if (data?.dailyBudget != null) {
      setBudgetInput(String(data.dailyBudget));
    }
  }, [data?.dailyBudget]);

  const saveMutation = useMutation({
    mutationFn: async (dailyBudget: number) => {
      const res = await adsApi.updateDailyBudget(profileId, dailyBudget);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to save daily budget');
      return res.data;
    },
    onSuccess: (payload: DailySpendPayload) => {
      queryClient.setQueryData(['daily-spend', profileId], payload);
      showToast('Daily budget saved', 'success');
    },
    onError: (err: Error) => {
      showToast(err.message || 'Failed to save daily budget', 'error');
    },
  });

  const onSave = (event: FormEvent) => {
    event.preventDefault();
    const parsed = Number(budgetInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast('Enter a daily budget greater than 0', 'error');
      return;
    }
    saveMutation.mutate(parsed);
  };

  const todayStatus = data?.todayStatus ?? 'unset';

  return (
    <div className="mx-auto max-w-7xl space-y-5 bg-page px-4 py-6 min-h-full">
      <div>
        <h1 className="text-headline-md font-semibold text-foreground">Daily spend</h1>
        <p className="mt-1 text-sm text-foreground-muted">
          Account-level spend versus the daily budget cap. Today and yesterday use
          Marketing Stream hourly totals until Amazon’s official daily report
          imports (usually two mornings later).
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-24 text-foreground-muted">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          Loading daily spend…
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-6 text-center">
          <p className="text-sm text-error-ink">
            {error instanceof Error ? error.message : 'Failed to load daily spend'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 text-sm font-semibold text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : null}

      {data ? (
        <>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-border bg-surface px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Today’s spend
              </p>
              <p className="mt-2 text-3xl font-semibold tabular-nums text-foreground">
                {formatCurrencyValue(data.todaySpend)}
              </p>
              <div className="mt-3">
                <Badge variant={statusBadgeVariant(todayStatus)} className="normal-case tracking-normal">
                  {statusLabel(todayStatus)}
                </Badge>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface px-5 py-4 lg:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Daily budget
              </p>
              {canWrite ? (
                <form onSubmit={onSave} className="mt-3 flex flex-wrap items-end gap-3">
                  <label className="min-w-[180px] flex-1">
                    <span className="sr-only">Daily budget</span>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={budgetInput}
                      onChange={(e) => setBudgetInput(e.target.value)}
                      placeholder="Amount to spend per day"
                    />
                  </label>
                  <Button type="submit" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? 'Saving…' : 'Save budget'}
                  </Button>
                </form>
              ) : (
                <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">
                  {data.dailyBudget == null ? 'Not set' : formatCurrencyValue(data.dailyBudget)}
                </p>
              )}
              <p className="mt-2 text-sm text-foreground-muted">
                Green is at or under this number. Yellow is up to 20% over. Red is more than 20%
                over.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-foreground-muted">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium text-right">Spend</th>
                  <th className="px-4 py-3 font-medium text-right">Daily budget</th>
                  <th className="px-4 py-3 font-medium text-right">% of budget</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((row) => (
                  <tr key={row.date} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{row.label}</div>
                      <div className="text-xs text-foreground-subtle">{row.date}</div>
                      {row.source === 'stream' ? (
                        <div className="mt-1 text-xs text-warning-ink">Provisional</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatCurrencyValue(row.spend)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.budget == null ? '—' : formatCurrencyValue(row.budget)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatPercentValue(row.spendPct)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={statusBadgeVariant(row.status)}
                        className="normal-case tracking-normal"
                      >
                        {statusLabel(row.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
